import React from "react";
import {
  VStack,
  HStack,
  Box,
  Button,
  Icon,
  Text,
  Spinner,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Tabs,
  TabList,
  Tab,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../../styles/datepicker.css";
import { AppIcons } from "constants/icons";
import Table, { TableColumn, TablePagination } from "components/common/Table";
import { AnalystRow, ChatMessageRow, MemoryRow, WorkflowRow } from "services/UsageMetricsService";

export type UsageType = "chats" | "workflows" | "analysts" | "memory";

const PAGE_SIZE = 50;

const CHAT_COLUMNS: TableColumn<ChatMessageRow>[] = [
  { key: "session_id", label: "Session ID", width: "180px" },
  { key: "session_title", label: "Session Title", width: "200px" },
  { key: "session_created_at", label: "Session Created At", width: "160px" },
  { key: "user_id", label: "User ID", width: "120px" },
  { key: "message_timestamp", label: "Message Timestamp", width: "160px" },
  { key: "analyst_id", label: "Analyst ID", width: "120px" },
  {
    key: "num_raw_dataframes",
    label: "Raw Dataframes",
    width: "110px",
    render: row => <Text textAlign="right">{row.num_raw_dataframes}</Text>,
  },
  {
    key: "num_workflows",
    label: "Workflows",
    width: "90px",
    render: row => <Text textAlign="right">{row.num_workflows}</Text>,
  },
  {
    key: "num_workflow_outputs",
    label: "Workflow Outputs",
    width: "120px",
    render: row => <Text textAlign="right">{row.num_workflow_outputs}</Text>,
  },
  {
    key: "num_code_snippets",
    label: "Code Snippets",
    width: "100px",
    render: row => <Text textAlign="right">{row.num_code_snippets}</Text>,
  },
  {
    key: "num_pandas_dataframes",
    label: "Pandas DFs",
    width: "90px",
    render: row => <Text textAlign="right">{row.num_pandas_dataframes}</Text>,
  },
  {
    key: "num_charts",
    label: "Charts",
    width: "80px",
    render: row => <Text textAlign="right">{row.num_charts}</Text>,
  },
  {
    key: "num_reports",
    label: "Reports",
    width: "80px",
    render: row => <Text textAlign="right">{row.num_reports}</Text>,
  },
  {
    key: "num_file_artifacts",
    label: "File Artifacts",
    width: "100px",
    render: row => <Text textAlign="right">{row.num_file_artifacts}</Text>,
  },
  {
    key: "num_workflow_runs",
    label: "Workflow Runs",
    width: "110px",
    render: row => <Text textAlign="right">{row.num_workflow_runs}</Text>,
  },
  {
    key: "num_workflow_run_searches",
    label: "Run Searches",
    width: "100px",
    render: row => <Text textAlign="right">{row.num_workflow_run_searches}</Text>,
  },
];

const WORKFLOW_COLUMNS: TableColumn<WorkflowRow>[] = [
  { key: "job_id", label: "Workflow ID", width: "180px" },
  { key: "job_name", label: "Workflow Name", width: "200px" },
  { key: "analyst_id", label: "Analyst ID", width: "120px" },
  { key: "author_id", label: "Author ID", width: "120px" },
  { key: "job_created_at", label: "Created At", width: "160px" },
  {
    key: "is_live",
    label: "Status",
    width: "90px",
    render: row => (
      <Text textAlign="center" color={row.is_live ? "green.500" : "gray.400"}>
        {row.is_live ? "Live" : "Paused"}
      </Text>
    ),
  },
  { key: "run_id", label: "Run ID", width: "180px" },
  { key: "run_created_at", label: "Run Created At", width: "160px" },
  { key: "run_status", label: "Run Status", width: "120px" },
];

const ANALYST_COLUMNS: TableColumn<AnalystRow>[] = [
  { key: "analyst_id", label: "Analyst ID", width: "180px" },
  { key: "analyst_name", label: "Analyst Name", width: "200px" },
  { key: "author_id", label: "Author ID", width: "120px" },
  { key: "created_at", label: "Created At", width: "160px" },
  { key: "last_modified_at", label: "Last Modified At", width: "160px" },
];

const MEMORY_COLUMNS: TableColumn<MemoryRow>[] = [
  { key: "memory_id", label: "Memory ID", width: "180px" },
  { key: "analyst_id", label: "Analyst ID", width: "180px" },
  { key: "source", label: "Source", width: "100px" },
  {
    key: "enabled",
    label: "Enabled",
    width: "90px",
    render: row => (
      <Text textAlign="center" color={row.enabled ? "green.500" : "gray.400"}>
        {row.enabled ? "Yes" : "No"}
      </Text>
    ),
  },
  { key: "added_by", label: "Added By", width: "120px" },
  { key: "created_at", label: "Created At", width: "160px" },
  { key: "session_id", label: "Session ID", width: "180px" },
];

interface UsageMetricsPanelProps {
  selectedType: UsageType;
  onTypeChange: (type: UsageType) => void;
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  dateError: string;
  onApply: () => void;
  rows: ChatMessageRow[] | WorkflowRow[] | AnalystRow[] | MemoryRow[];
  total: number;
  offset: number;
  hasMore: boolean;
  loading: boolean;
  onPageChange: (offset: number) => void;
  isDownloading: boolean;
  onDownloadCsv: () => void;
}

const UsageMetricsPanel: React.FC<UsageMetricsPanelProps> = ({
  selectedType,
  onTypeChange,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  dateError,
  onApply,
  rows,
  total,
  offset,
  hasMore,
  loading,
  onPageChange,
  isDownloading,
  onDownloadCsv,
}) => {
  const pagination: TablePagination = { total, offset, has_more: hasMore };
  const tabIndex = { chats: 0, workflows: 1, analysts: 2, memory: 3 }[selectedType] ?? 0;
  const TAB_TYPES: UsageType[] = ["chats", "workflows", "analysts", "memory"];

  return (
    <VStack spacing={3} p={5} height="100%" alignItems="stretch">
      {/* Header */}
      <HStack justify="space-between" w="full" px={3} flexShrink={0}>
        <HStack spacing={4}>
          <Icon as={AppIcons.evaUsageMetrics} boxSize={7} color="fg.default" />
          <Text fontSize="lg" mb={0} fontWeight="700">
            Usage Metrics
          </Text>
        </HStack>
        <Button
          onClick={onDownloadCsv}
          isLoading={isDownloading}
          isDisabled={!startDate || !endDate || !!dateError || isDownloading}
          colorScheme="purple"
          size="md"
          bg="purple.700"
          color="white"
          _hover={{ bg: "purple.600" }}
          leftIcon={<Icon as={AppIcons.download} boxSize={4} color="white" />}
        >
          Download CSV
        </Button>
      </HStack>

      {/* Type selector */}
      <Box px={3} flexShrink={0}>
        <Tabs
          index={tabIndex}
          onChange={index => onTypeChange(TAB_TYPES[index])}
          variant="soft-rounded"
          colorScheme="purple"
          size="sm"
        >
          <TabList>
            <Tab>Chats</Tab>
            <Tab>Workflows</Tab>
            <Tab>Analysts</Tab>
            <Tab>Memory</Tab>
          </TabList>
        </Tabs>
      </Box>

      {/* Date range filter — shown only for Chats */}
      <HStack px={3} spacing={6} flexShrink={0} align="flex-end">
        {selectedType === "chats" && (
          <>
            <FormControl isInvalid={!!dateError} w="auto">
              <FormLabel fontSize="sm" mb={1} color="fg.muted">
                Start Date
              </FormLabel>
              <Box
                border="1px solid"
                borderColor="border.default"
                borderRadius="md"
                px={3}
                py={1.5}
                bg="bg.surface"
                minW="140px"
              >
                <DatePicker
                  selected={startDate}
                  onChange={onStartDateChange}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="Select start date"
                  maxDate={endDate ?? new Date()}
                  popperProps={{ strategy: "fixed" }}
                  popperPlacement="bottom-start"
                />
              </Box>
              {dateError && <FormErrorMessage>{dateError}</FormErrorMessage>}
            </FormControl>

            <FormControl w="auto">
              <FormLabel fontSize="sm" mb={1} color="fg.muted">
                End Date
              </FormLabel>
              <Box
                border="1px solid"
                borderColor="border.default"
                borderRadius="md"
                px={3}
                py={1.5}
                bg="bg.surface"
                minW="140px"
              >
                <DatePicker
                  selected={endDate}
                  onChange={onEndDateChange}
                  dateFormat="dd-MMM-yyyy"
                  placeholderText="Select end date"
                  minDate={startDate ?? undefined}
                  maxDate={new Date()}
                  popperProps={{ strategy: "fixed" }}
                  popperPlacement="bottom-start"
                />
              </Box>
            </FormControl>

            <Button
              onClick={onApply}
              isDisabled={!startDate || !endDate || !!dateError}
              colorScheme="purple"
              variant="outline"
              size="md"
              mb={0}
            >
              Apply
            </Button>
          </>
        )}

        {loading && <Spinner size="sm" color="purple.500" />}

        {!loading && total > 0 && (
          <Text fontSize="sm" color="fg.muted" mb={0}>
            {total.toLocaleString()} rows total
          </Text>
        )}
      </HStack>

      {/* Table */}
      <Box flex="1" overflow="hidden" width="100%">
        {selectedType === "chats" ? (
          <Table<ChatMessageRow>
            data={rows as ChatMessageRow[]}
            columns={CHAT_COLUMNS}
            loading={loading}
            pagination={pagination}
            limit={PAGE_SIZE}
            onPageChange={onPageChange}
            emptyMessage="No data for the selected date range"
            emptySubMessage="Adjust the date range and click Apply"
          />
        ) : selectedType === "workflows" ? (
          <Table<WorkflowRow>
            data={rows as WorkflowRow[]}
            columns={WORKFLOW_COLUMNS}
            loading={loading}
            pagination={pagination}
            limit={PAGE_SIZE}
            onPageChange={onPageChange}
            emptyMessage="No workflows found"
          />
        ) : selectedType === "analysts" ? (
          <Table<AnalystRow>
            data={rows as AnalystRow[]}
            columns={ANALYST_COLUMNS}
            loading={loading}
            pagination={pagination}
            limit={PAGE_SIZE}
            onPageChange={onPageChange}
            emptyMessage="No analysts found"
          />
        ) : (
          <Table<MemoryRow>
            data={rows as MemoryRow[]}
            columns={MEMORY_COLUMNS}
            loading={loading}
            pagination={pagination}
            limit={PAGE_SIZE}
            onPageChange={onPageChange}
            emptyMessage="No memory facts found"
          />
        )}
      </Box>
    </VStack>
  );
};

export default UsageMetricsPanel;
